import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { AlertCircle, ArrowLeft, Calendar, DollarSign, MapPin, FileText, Clock, HelpCircle, Gavel, Timer, CalendarCheck, Eye, CheckCircle, ArrowRight, Info } from "lucide-react";
import { formatWithTimezoneLabel, isDeadlinePassed } from "@/lib/timezone";
import { Alert, AlertDescription } from "@/components/ui/alert";

interface RFPDetails {
  id: string;
  title: string;
  description: string;
  category: string | null;
  budget_range: string | null;
  submission_deadline: string;
  auction_start: string;
  auction_end: string;
  status: string;
  matter_name: string | null;
  region: string | null;
  team: string | null;
  questions_data: any;
  pricing_data: any;
  min_bid: number | null;
  min_decrement: number | null;
}

interface ProposalDetails {
  id: string;
  initial_bid_amount: number;
  submitted_at: string;
  selection_status: string;
  supplier_company_name: string;
  contact_name: string;
  contact_email: string;
  proposal_details: string | null;
  question_responses: any;
  final_bid_amount?: number;
}

// Helper to capitalize words properly, keeping acronyms uppercase
const formatRegion = (str: string) => {
  const acronyms = ['mena', 'emea', 'apac', 'latam', 'uk', 'us', 'uae'];
  if (acronyms.includes(str.toLowerCase())) {
    return str.toUpperCase();
  }
  return str.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

// Helper to capitalize words properly
const capitalizeWords = (str: string) => {
  return str.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase());
};

const SupplierRFPView = () => {
  const { rfpId, token } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [rfp, setRfp] = useState<RFPDetails | null>(null);
  const [proposal, setProposal] = useState<ProposalDetails | null>(null);
  const [loading, setLoading] = useState(true);

  // Always redirect to signin if not authenticated - never assume signed in
  useEffect(() => {
    if (!authLoading && !user) {
      const currentPath = `/supplier/rfp/${rfpId}/view/${token}`;
      navigate(`/signin?redirect=${encodeURIComponent(currentPath)}`, { replace: true });
    }
  }, [authLoading, user, rfpId, token, navigate]);

  useEffect(() => {
    // Only fetch RFP details after user is confirmed authenticated
    if (!authLoading && user) {
      fetchRFPDetails();
    }
  }, [rfpId, token, user, authLoading]);

  const fetchRFPDetails = async () => {
    try {
      // Verify invitation
      const { data: invitation, error: invError } = await supabase
        .from("supplier_invitations")
        .select("id")
        .eq("invite_token", token!)
        .eq("rfp_id", rfpId!)
        .single();

      if (invError || !invitation) {
        throw new Error("Invalid invitation");
      }

      // Fetch RFP
      const { data: rfpData, error: rfpError } = await supabase
        .from("rfps")
        .select("*")
        .eq("id", rfpId!)
        .single();

      if (rfpError) throw rfpError;
      setRfp(rfpData);

      // Fetch proposal if exists
      const { data: proposalData } = await supabase
        .from("proposals")
        .select("*")
        .eq("invitation_id", invitation.id)
        .maybeSingle();

      if (proposalData) {
        // Fetch the lowest confirmed bid for this proposal (final bid)
        const { data: bidsData } = await supabase
          .from("bids")
          .select("amount")
          .eq("proposal_id", proposalData.id)
          .eq("status", "confirmed")
          .order("amount", { ascending: true })
          .limit(1);

        const finalBidAmount = bidsData && bidsData.length > 0 
          ? bidsData[0].amount 
          : proposalData.initial_bid_amount;

        setProposal({
          ...proposalData,
          final_bid_amount: finalBidAmount
        });
      }
    } catch (error: any) {
      console.error("Error fetching RFP:", error);
      toast({
        title: "Error",
        description: "Failed to load RFP details",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return null; // Will redirect
  }

  if (!rfp) {
    return (
      <div className="flex items-center justify-center h-full bg-background">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="h-12 w-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">RFP Not Found</h2>
            <p className="text-muted-foreground">This RFP does not exist or you do not have access.</p>
            <Button onClick={() => navigate("/supplier/dashboard")} className="mt-4">
              Return to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Determine if reverse auction is enabled (has min_bid or min_decrement set)
  const hasReverseAuction = rfp.min_bid !== null || rfp.min_decrement !== null;
  
  const submissionClosed = isDeadlinePassed(rfp.submission_deadline);
  const auctionStarted = isDeadlinePassed(rfp.auction_start);
  const auctionEnded = isDeadlinePassed(rfp.auction_end);
  const isSuccessful = proposal?.selection_status === "successful";
  const isCompleted = rfp.status === "COMPLETED" || isSuccessful;
  const proposalDetails = proposal?.proposal_details ? JSON.parse(proposal.proposal_details) : null;
  const questions = rfp.questions_data?.questions || [];

  // Format date for timeline display
  const formatTimelineDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = months[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    return `${month} ${day}, ${year}`;
  };

  // Format time for timeline display
  const formatTimelineTime = (dateStr: string) => {
    const date = new Date(dateStr);
    const hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes} ${ampm} GMT`;
  };

  return (
    <div className="bg-background p-6 min-h-full">
      <div className="max-w-4xl mx-auto space-y-6 pb-6">
        <Button variant="ghost" onClick={() => navigate("/supplier/dashboard")} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>

        {/* Completed Auction Banner */}
        {(auctionEnded || isCompleted) && proposal && (
          <Alert className={isSuccessful ? "border-green-300 bg-green-50" : "border-blue-300 bg-blue-50"}>
            <Info className={`h-4 w-4 ${isSuccessful ? "text-green-600" : "text-blue-600"}`} />
            <AlertDescription className="flex items-center justify-between">
              <span className={isSuccessful ? "text-green-700" : "text-blue-700"}>
                {isSuccessful 
                  ? "Congratulations! You won this auction. View all completed matters in Closed Auctions."
                  : "This auction has ended. View all completed matters in Closed Auctions."}
              </span>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => navigate("/supplier/closed-auctions")}
                className={isSuccessful ? "border-green-400 text-green-700 hover:bg-green-100" : "border-blue-400 text-blue-700 hover:bg-blue-100"}
              >
                View Closed Auctions
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* RFP Header */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{rfp.matter_name || rfp.title}</CardTitle>
                <p className="text-muted-foreground mt-2">{rfp.description}</p>
              </div>
              <Badge variant={submissionClosed ? "secondary" : "default"}>
                {rfp.status.replace(/_/g, " ")}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Key Information Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rfp.category && (
                <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                  <FileText className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Category</p>
                    <p className="font-medium">{capitalizeWords(rfp.category)}</p>
                  </div>
                </div>
              )}
              {rfp.budget_range && (
                <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                  <DollarSign className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Budget Range</p>
                    <p className="font-medium">{rfp.budget_range}</p>
                  </div>
                </div>
              )}
              {rfp.region && (
                <div className="flex items-center gap-3 p-4 bg-muted/50 rounded-lg">
                  <MapPin className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm text-muted-foreground">Region</p>
                    <p className="font-medium">{formatRegion(rfp.region)}</p>
                  </div>
                </div>
              )}
            </div>

            {/* Pricing Information */}
            {rfp.pricing_data && (
              <>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-4 flex items-center gap-2">
                    <DollarSign className="h-5 w-5" />
                    Pricing Information
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {rfp.pricing_data.priceArrangement && (
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Price Arrangement</p>
                        <p className="font-medium">{capitalizeWords(rfp.pricing_data.priceArrangement)}</p>
                      </div>
                    )}
                    {rfp.pricing_data.currency && (
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Currency</p>
                        <p className="font-medium">{rfp.pricing_data.currency === 'custom' ? rfp.pricing_data.customCurrency : rfp.pricing_data.currency.toUpperCase()}</p>
                      </div>
                    )}
                    {rfp.pricing_data.provideEstimate && rfp.pricing_data.estimateType === 'fixed' && rfp.pricing_data.estimatePrice && (
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Estimated Price</p>
                        <p className="font-medium">${Number(rfp.pricing_data.estimatePrice).toLocaleString()}</p>
                      </div>
                    )}
                    {rfp.pricing_data.provideEstimate && rfp.pricing_data.estimateType === 'range' && (rfp.pricing_data.estimatePriceMin || rfp.pricing_data.estimatePriceMax) && (
                      <div className="p-4 border rounded-lg">
                        <p className="text-sm text-muted-foreground">Estimated Range</p>
                        <p className="font-medium">
                          ${Number(rfp.pricing_data.estimatePriceMin || 0).toLocaleString()} - ${Number(rfp.pricing_data.estimatePriceMax || 0).toLocaleString()}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}

            <Separator />

            {/* Timeline - Conditional based on Reverse Auction */}
            {hasReverseAuction ? (
              // Scenario A: With Reverse Auction - Live Auctions style timeline (without Request Published when viewing proposal)
              <Card>
                <CardContent className="py-6">
                  <div className={`grid ${proposal ? 'grid-cols-3' : 'grid-cols-4'} gap-4`}>
                    {/* Request Published - Only show when no proposal submitted */}
                    {!proposal && (
                      <div className="text-center">
                        <div className="flex justify-center mb-3">
                          <div className="p-3 rounded-lg bg-blue-50">
                            <Calendar className="h-8 w-8 text-blue-500" />
                          </div>
                        </div>
                        <p className="font-semibold text-sm">Request published</p>
                        <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp.submission_deadline)}</p>
                        <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.submission_deadline)}</p>
                      </div>
                    )}

                    {/* Proposals Due */}
                    <div className="text-center relative">
                      <div className="flex justify-center mb-3">
                        <div className="p-3 rounded-lg bg-blue-50">
                          <CalendarCheck className="h-8 w-8 text-blue-500" />
                        </div>
                      </div>
                      <p className="font-semibold text-sm">Proposals due</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp.submission_deadline)}</p>
                      <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.submission_deadline)}</p>
                      {submissionClosed && (
                        <Badge variant="secondary" className="mt-2">Closed</Badge>
                      )}
                    </div>

                    {/* Auction Start */}
                    <div className="text-center relative">
                      <div className="flex justify-center mb-3">
                        <div className={`p-3 rounded-lg ${isCompleted ? 'bg-gray-100' : auctionStarted ? 'bg-green-50' : 'bg-blue-50'}`}>
                          <Gavel className={`h-8 w-8 ${isCompleted ? 'text-gray-500' : auctionStarted ? 'text-green-500' : 'text-blue-500'}`} />
                        </div>
                      </div>
                      <p className="font-semibold text-sm">Auction start</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp.auction_start)}</p>
                      <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.auction_start)}</p>
                      {isCompleted ? (
                        <Badge variant="secondary" className="mt-2">Finished</Badge>
                      ) : auctionStarted ? (
                        <Badge className="mt-2 bg-green-500">Started</Badge>
                      ) : (
                        <Badge variant="secondary" className="mt-2">Pending</Badge>
                      )}
                    </div>

                    {/* Auction End */}
                    <div className="text-center relative">
                      <div className="flex justify-center mb-3">
                        <div className={`p-3 rounded-lg ${auctionEnded || isCompleted ? 'bg-gray-100' : 'bg-blue-50'}`}>
                          <Timer className={`h-8 w-8 ${auctionEnded || isCompleted ? 'text-gray-500' : 'text-blue-500'}`} />
                        </div>
                      </div>
                      <p className="font-semibold text-sm">Auction end</p>
                      <p className="text-xs text-muted-foreground mt-1">{formatTimelineDate(rfp.auction_end)}</p>
                      <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.auction_end)}</p>
                      {(auctionEnded || isCompleted) && (
                        <Badge variant="secondary" className="mt-2">Finished</Badge>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ) : (
              // Scenario B: No Reverse Auction - Simplified timeline with all dates
              <div>
                <h3 className="font-semibold mb-4 flex items-center gap-2">
                  <Calendar className="h-5 w-5" />
                  Timeline
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Request Published</p>
                    <p className="font-medium">{formatTimelineDate(rfp.submission_deadline)}</p>
                    <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.submission_deadline)}</p>
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Proposals Deadline</p>
                    <p className="font-medium">{formatTimelineDate(rfp.submission_deadline)}</p>
                    <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.submission_deadline)}</p>
                    {submissionClosed && (
                      <Badge variant="destructive" className="mt-2">Closed</Badge>
                    )}
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Auction Start</p>
                    <p className="font-medium">{formatTimelineDate(rfp.auction_start)}</p>
                    <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.auction_start)}</p>
                    {auctionStarted && !auctionEnded && (
                      <Badge className="mt-2 bg-green-500">Live</Badge>
                    )}
                  </div>
                  <div className="p-4 border rounded-lg">
                    <p className="text-sm text-muted-foreground">Auction Deadline</p>
                    <p className="font-medium">{formatTimelineDate(rfp.auction_end)}</p>
                    <p className="text-xs text-muted-foreground">{formatTimelineTime(rfp.auction_end)}</p>
                    {auctionEnded && (
                      <Badge variant="secondary" className="mt-2">Ended</Badge>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Questions */}
            {questions.length > 0 && (
              <>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-4">Client Questions</h3>
                  <div className="space-y-4">
                    {questions.map((q: any, index: number) => (
                      <div key={index} className="p-4 border rounded-lg">
                        <p className="font-medium">{q.question}</p>
                        {q.type && <Badge variant="outline" className="mt-2">{q.type}</Badge>}
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Invite Additional Suppliers - Only for non-RFP view without reverse auction and no proposal */}
        {!hasReverseAuction && !proposal && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Invite Additional Suppliers</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground text-sm">
                Contact your in-house team to add more suppliers to this RFP.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Your Proposal */}
        {proposal && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Your Proposal</span>
                <Badge 
                  variant={proposal.selection_status === "successful" ? "default" : 
                           proposal.selection_status === "unsuccessful" ? "destructive" : "secondary"}
                >
                  {proposal.selection_status === "successful" ? "Awarded" :
                   proposal.selection_status === "unsuccessful" ? "Unsuccessful" : "Pending"}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Company</p>
                  <p className="font-medium">{proposal.supplier_company_name}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Initial Bid</p>
                  <p className="font-medium text-lg">${proposal.initial_bid_amount.toLocaleString()}</p>
                </div>
                {/* Show Current Bid if different from initial */}
                {proposal.final_bid_amount && proposal.final_bid_amount !== proposal.initial_bid_amount && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">Current Bid</p>
                    <p className="font-medium text-lg text-green-600">${proposal.final_bid_amount.toLocaleString()}</p>
                  </div>
                )}
                {/* Show Current Bid section for successful proposals */}
                {isSuccessful && proposal.final_bid_amount === proposal.initial_bid_amount && (
                  <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">Current Bid</p>
                    <p className="font-medium text-lg text-green-600">${proposal.final_bid_amount?.toLocaleString()}</p>
                  </div>
                )}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Contact</p>
                  <p className="font-medium">{proposal.contact_name}</p>
                  <p className="text-sm text-muted-foreground">{proposal.contact_email}</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg">
                  <p className="text-sm text-muted-foreground">Submitted</p>
                  <p className="font-medium">{formatWithTimezoneLabel(proposal.submitted_at)}</p>
                </div>
              </div>

              {proposalDetails && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <h4 className="font-semibold">Summary</h4>
                    {proposalDetails.scope && (
                      <div>
                        <h4 className="font-medium text-muted-foreground mb-2">Scope</h4>
                        <p className="whitespace-pre-wrap">{proposalDetails.scope}</p>
                      </div>
                    )}
                    {proposalDetails.deliverables && (
                      <div>
                        <h4 className="font-medium text-muted-foreground mb-2">Deliverables</h4>
                        <p className="whitespace-pre-wrap">{proposalDetails.deliverables}</p>
                      </div>
                    )}
                    {proposalDetails.exemptions && (
                      <div>
                        <h4 className="font-medium text-muted-foreground mb-2">Exemptions</h4>
                        <p className="whitespace-pre-wrap">{proposalDetails.exemptions}</p>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Question Responses */}
              {proposal.question_responses && Array.isArray(proposal.question_responses) && proposal.question_responses.length > 0 && (
                <>
                  <Separator />
                  <div className="space-y-4">
                    <div className="flex items-center gap-2">
                      <HelpCircle className="h-5 w-5 text-blue-600" />
                      <h4 className="font-semibold">Your Responses to Client Questions</h4>
                    </div>
                    {proposal.question_responses.map((qr: any, index: number) => (
                      <div key={index} className="p-4 border rounded-lg bg-gray-50">
                        <p className="font-medium text-sm">
                          {index + 1}. {qr.question}
                          {qr.isRequired && <span className="text-red-500 ml-1">*</span>}
                        </p>
                        <p className="mt-2 text-gray-700 whitespace-pre-wrap">{qr.response || <span className="text-muted-foreground italic">No response provided</span>}</p>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Action Buttons */}
        <div className="flex gap-4 flex-wrap">
          {!proposal && !auctionStarted && (
            <Button onClick={() => navigate(`/rfp/${rfpId}/respond/${token}`)}>
              Submit Proposal
            </Button>
          )}
          {/* Show "Auction Completed" if user won */}
          {proposal && isSuccessful && (
            <Button variant="outline" disabled className="bg-green-50 border-green-300 text-green-700">
              <CheckCircle className="h-4 w-4 mr-2" />
              Auction Completed - You Won!
            </Button>
          )}
          {/* Show "Join Auction" only if auction is active and user hasn't won */}
          {proposal && auctionStarted && !auctionEnded && !isCompleted && (
            <Button onClick={() => navigate(`/rfp/${rfpId}/bid/${token}`)}>
              <Gavel className="h-4 w-4 mr-2" />
              Join Auction
            </Button>
          )}
          {/* Show "Auction Ended" if auction ended but user didn't win */}
          {proposal && (auctionEnded || isCompleted) && !isSuccessful && (
            <Button variant="outline" disabled>
              <Clock className="h-4 w-4 mr-2" />
              Auction Ended
            </Button>
          )}
          {proposal && !auctionStarted && !isCompleted && (
            <Button variant="outline" disabled>
              <Clock className="h-4 w-4 mr-2" />
              Auction Not Started Yet
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default SupplierRFPView;
